import { useEffect, useState } from "react";
import { api } from "@/api/client";
import MasterCrudPage from "@/components/MasterCrudPage";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Key, Search, X } from "lucide-react";
import { toast } from "sonner";
import { promptDialog } from "@/components/ui/confirm";

export default function Users() {
  const [roles, setRoles] = useState([]);
  const [depts, setDepts] = useState([]);

  // filter state
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    api.get("/roles").then(r => setRoles(r.data?.data || []));
    api.get("/departments").then(r => setDepts(r.data?.data || []));
  }, []);

  const resetPw = async (row) => {
    const newPw = await promptDialog({ title: "Reset password", message: `New password for ${row.name} (min 8 characters):`, inputType: "password", placeholder: "New password", confirmText: "Reset" });
    if (newPw === null) return;
    if (newPw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    try {
      await api.post(`/users/${row.id}/reset-password`, { new_password: newPw });
      toast.success("Password reset");
    } catch (e) { toast.error(e?.response?.data?.message || "Failed"); }
  };

  const queryParams = {
    ...(search ? { search } : {}),
    ...(deptFilter ? { department_id: deptFilter } : {}),
    ...(roleFilter ? { role_id: roleFilter } : {}),
    ...(statusFilter !== "" ? { is_active: statusFilter === "active" } : {}),
    limit: 50,
  };

  const filterBar = (
    <div className="erp-card p-3 flex flex-wrap items-center gap-2">
      {/* Search */}
      <div className="relative min-w-[200px] flex-1 max-w-xs">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search name, email, code..."
          className="erp-input pl-8 text-sm"
          data-testid="users-search"
        />
        {search && <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X size={12} /></button>}
      </div>

      {/* Department */}
      <div className="min-w-[160px]">
        <SearchableSelect
          options={depts.map(d => ({ value: d.id, label: d.name }))}
          value={deptFilter}
          onValueChange={setDeptFilter}
          placeholder="All Departments"
          allowClear
        />
      </div>

      {/* Role */}
      <div className="min-w-[140px]">
        <SearchableSelect
          options={roles.map(r => ({ value: r.id, label: r.name }))}
          value={roleFilter}
          onValueChange={setRoleFilter}
          placeholder="All Roles"
          allowClear
        />
      </div>

      {/* Status */}
      <div className="flex rounded-md border border-slate-300 overflow-hidden text-xs font-medium">
        {[["", "All"], ["active", "Active"], ["inactive", "Inactive"]].map(([val, label]) => (
          <button key={val} onClick={() => setStatusFilter(val)}
            className={`px-3 py-2 transition-colors ${statusFilter === val ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
            {label}
          </button>
        ))}
      </div>

      {(search || deptFilter || roleFilter || statusFilter) && (
        <button onClick={() => { setSearch(""); setDeptFilter(""); setRoleFilter(""); setStatusFilter(""); }}
          className="text-xs text-slate-500 hover:text-slate-800 underline whitespace-nowrap">
          Clear filters
        </button>
      )}
    </div>
  );

  return (
    <MasterCrudPage
      endpoint="/users"
      title="Users"
      breadcrumbs={["Masters", "Users"]}
      testIdPrefix="users"
      initialForm={{ is_active: true, team_ids: [] }}
      queryParams={queryParams}
      filterBar={filterBar}
      columns={[
        { key: "employee_code", label: "Emp Code", width: 110 },
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "role_name", label: "Role" },
        { key: "department_name", label: "Department" },
        { key: "whatsapp_number", label: "WhatsApp" },
        { key: "is_active", label: "Status", render: (r) => r.is_active ?
          <span className="badge-base bg-emerald-50 text-emerald-700 border-emerald-200">Active</span> :
          <span className="badge-base bg-slate-100 text-slate-500 border-slate-200">Inactive</span>
        },
      ]}
      formFields={[
        { key: "name", label: "Name", required: true },
        { key: "email", label: "Email", type: "email", required: true },
        { key: "password", label: "Password (leave blank to keep)", type: "password" },
        { key: "employee_code", label: "Employee Code" },
        { key: "whatsapp_number", label: "WhatsApp Number", placeholder: "+91..." },
        { key: "role_id", label: "Role", type: "select", required: true,
          options: roles.map(r => ({ value: r.id, label: r.name })) },
        { key: "department_id", label: "Department", type: "select",
          options: depts.map(d => ({ value: d.id, label: d.name })) },
        { key: "designation", label: "Designation" },
        { key: "is_active", label: "Active", type: "checkbox" },
      ]}
      beforeSubmit={(p) => { if (!p.password) delete p.password; return p; }}
      customActions={(row) => (
        <button onClick={() => resetPw(row)} className="p-1.5 text-slate-600 hover:bg-slate-100 rounded-sm" title="Reset password" data-testid={`reset-pw-${row.id}`}>
          <Key size={14} />
        </button>
      )}
    />
  );
}
